/**
 * Nexus User Detail Page
 * View and manage individual user details including billing history
 */

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { nexusUsersApi } from '@/services/nexusApi'
import { ArrowLeft, Mail, Phone, Calendar, Wallet, CreditCard, ChevronDown, ChevronUp } from 'lucide-react'

interface UserDetail {
  id: string
  email: string
  username: string
  full_name: string | null
  phone_number: string | null
  role: string
  is_active: boolean
  is_verified: boolean
  niki_gender: string
  wallet_balance: number | null
  created_at: string
  last_login_at: string | null
  ability_overrides: any[]
}

interface ChargeTransaction {
  id: string
  ability_name: string
  gross_amount: number
  description: string | null
  created_at: string
}

interface AbilityCharges {
  ability_name: string
  transaction_count: number
  total_gross: number
  total_cost: number
  total_revenue: number
  total_mpesa_fee: number
  transactions: ChargeTransaction[]
}

interface UserCharges {
  user_id: string
  user_email: string
  user_name: string
  abilities: AbilityCharges[]
  totals: {
    total_gross: number
    total_cost: number
    total_revenue: number
    total_mpesa_fee: number
    total_transactions: number
  }
}

const ABILITY_DISPLAY_NAMES: Record<string, string> = {
  airtime: 'Airtime Purchase',
  send_sms: 'SMS Messages',
  send_money: 'Money Transfer',
  web_search: 'Web Search',
  youtube: 'YouTube',
  email: 'Email',
}

export default function NexusUserDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [user, setUser] = useState<UserDetail | null>(null)
  const [charges, setCharges] = useState<UserCharges | null>(null)
  const [loading, setLoading] = useState(true)
  const [chargesLoading, setChargesLoading] = useState(true)
  const [expandedAbility, setExpandedAbility] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      loadUser()
      loadCharges()
    }
  }, [id])

  const loadUser = async () => {
    try {
      const data = await nexusUsersApi.get(id!)
      setUser(data)
    } catch (error) {
      console.error('Failed to load user:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadCharges = async () => {
    setChargesLoading(true)
    try {
      const data = await nexusUsersApi.getUserCharges(id!)
      setCharges(data)
    } catch (error) {
      console.error('Failed to load charges:', error)
    } finally {
      setChargesLoading(false)
    }
  }

  const toggleAbility = (abilityName: string) => {
    setExpandedAbility(expandedAbility === abilityName ? null : abilityName)
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getDisplayName = (ability: string) => {
    return ABILITY_DISPLAY_NAMES[ability] || ability.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-700 rounded w-48" />
          <div className="h-32 bg-slate-800 rounded-xl" />
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="p-8">
        <p className="text-red-400">User not found</p>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/nexus/users')}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">
            {user.full_name || user.username}
          </h1>
          <p className="text-gray-400">{user.email}</p>
        </div>
        <div className="ml-auto flex gap-2">
          <span className={`px-3 py-1 rounded-full text-sm ${
            user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {user.is_active ? 'Active' : 'Inactive'}
          </span>
          <span className={`px-3 py-1 rounded-full text-sm ${
            user.is_verified ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'
          }`}>
            {user.is_verified ? 'Verified' : 'Unverified'}
          </span>
        </div>
      </div>

      {/* User Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-3 mb-2">
            <Mail className="w-5 h-5 text-purple-400" />
            <span className="text-sm text-gray-400">Email</span>
          </div>
          <p className="text-white font-medium truncate">{user.email}</p>
        </div>

        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-3 mb-2">
            <Phone className="w-5 h-5 text-purple-400" />
            <span className="text-sm text-gray-400">Phone</span>
          </div>
          <p className="text-white font-medium">{user.phone_number || 'Not set'}</p>
        </div>

        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-3 mb-2">
            <Wallet className="w-5 h-5 text-green-400" />
            <span className="text-sm text-gray-400">Wallet Balance</span>
          </div>
          <p className="text-white font-medium">
            KES {(user.wallet_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
        </div>

        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-purple-400" />
            <span className="text-sm text-gray-400">Last Login</span>
          </div>
          <p className="text-white font-medium text-sm">
            {user.last_login_at ? formatDate(user.last_login_at) : 'Never'}
          </p>
        </div>
      </div>

      {/* Billing History Section */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-purple-400" />
            <h2 className="text-lg font-semibold text-white">Billing History</h2>
          </div>
          {charges && charges.totals.total_transactions > 0 && (
            <div className="text-sm text-gray-400">
              {charges.totals.total_transactions} transactions |
              <span className="text-white ml-1">
                KES {charges.totals.total_gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>

        {chargesLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto" />
            <p className="text-gray-400 mt-4">Loading billing history...</p>
          </div>
        ) : !charges || charges.abilities.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No billing history found for this user
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {charges.abilities.map((ability) => (
              <div key={ability.ability_name}>
                {/* Ability Header */}
                <button
                  onClick={() => toggleAbility(ability.ability_name)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-medium">{getDisplayName(ability.ability_name)}</p>
                      <p className="text-sm text-gray-400">{ability.transaction_count} transactions</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-white font-medium">
                        KES {ability.total_gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                      {ability.total_mpesa_fee > 0 && (
                        <p className="text-xs text-gray-500">
                          Incl. M-Pesa fees: KES {ability.total_mpesa_fee.toLocaleString()}
                        </p>
                      )}
                    </div>
                    {expandedAbility === ability.ability_name ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Transactions */}
                {expandedAbility === ability.ability_name && ability.transactions.length > 0 && (
                  <div className="bg-slate-900/50 border-t border-slate-700">
                    <table className="w-full">
                      <thead>
                        <tr className="text-xs text-gray-500 uppercase">
                          <th className="px-6 py-3 text-left">Date</th>
                          <th className="px-6 py-3 text-left">Description</th>
                          <th className="px-6 py-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {ability.transactions.map((tx) => (
                          <tr key={tx.id} className="hover:bg-slate-800/50">
                            <td className="px-6 py-3 text-sm text-gray-400">
                              {formatDate(tx.created_at)}
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-300">
                              {tx.description || '-'}
                            </td>
                            <td className="px-6 py-3 text-sm text-white text-right">
                              KES {tx.gross_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}

            {/* Totals Footer */}
            <div className="px-6 py-4 bg-slate-900/50">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Total Billed</span>
                <span className="text-xl font-bold text-white">
                  KES {charges.totals.total_gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
              {charges.totals.total_mpesa_fee > 0 && (
                <div className="flex justify-between items-center mt-1">
                  <span className="text-sm text-gray-500">M-Pesa B2C Fees (pass-through)</span>
                  <span className="text-sm text-gray-400">
                    KES {charges.totals.total_mpesa_fee.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
